import { useQuery } from '@apollo/client';
import { gql } from '__generated__';
import { DepartmentType } from '__generated__/graphql';
import { FC } from 'react';
import { useNavigate } from 'react-router-dom';

import SidebarLayout from 'layouts/SidebarLayout';

import DataTable from 'components/DataTable';
import ErrorMessage from 'components/ErrorMessage';
import LoadingIndicator from 'components/LoadingIndicator';
import Navbar from 'components/Navbar';

import CreateDepartmentDialogButton from './CreateDepartmentDialogButton';

const DEPARTMENTS_QUERY = gql(`
  query Departments($cursor: ID, $limit: Int, $filters: DepartmentsFilterType) {
    departments(cursor: $cursor, limit: $limit, filters: $filters) {
      nodes {
        id
        name
        shortName
        status
        logo
        updatedAt
        createdAt
      }
      pageInfo {
        cursor
        totalCount
        hasNextPage
      }
    }
  }
`);

const DepartmentsPage: FC = () => {
  const navigate = useNavigate();
  const { loading, error, data, refetch, fetchMore } = useQuery(DEPARTMENTS_QUERY, {
    variables: {
      limit: 30,
    },
    notifyOnNetworkStatusChange: true,
  });

  const renderContent = () => {
    if (loading && !data) return <LoadingIndicator />;

    if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

    const departments = data.departments.nodes;
    const pageInfo = data.departments.pageInfo;

    return (
      <div>
        <CreateDepartmentDialogButton />
        <DataTable
          data={departments as DepartmentType[]}
          searchFields={['name']}
          onClick={department => navigate(`/departments/${department.id}`)}
          columns={[
            {
              label: 'Name',
              fieldName: 'name',
            },
            {
              label: 'Short Name',
              fieldName: 'shortName',
            },
            {
              label: 'Logo',
              fieldName: 'logo',
              type: 'IMAGE',
            },
            {
              label: 'Status',
              fieldName: 'status',
              type: 'STATUS',
            },
            {
              label: 'Created At',
              fieldName: 'createdAt',
              type: 'DATETIME',
            },
            {
              label: 'Updated At',
              fieldName: 'updatedAt',
              type: 'DATETIME',
            },
          ]}
          hasNextPage={pageInfo.hasNextPage as boolean}
          paginationLoading={loading}
          onLoadMore={() =>
            fetchMore({
              variables: {
                cursor: pageInfo.cursor,
              },
            })
          }
        />
      </div>
    );
  };

  return (
    <SidebarLayout>
      <Navbar title="Departments" />
      {renderContent()}
    </SidebarLayout>
  );
};

export default DepartmentsPage;
